import type { BrandSpecial } from '@iheartjane/data-access';
import type { SmartSortProduct } from '@iheartjane/dm-sdk';
import { getPriceIdsForBrandSpecial } from '@iheartjane/menu';
import type {
  PriceId,
  ProductWeight,
  QualifyingStatusEnum,
} from '@iheartjane/types';

import type { AlgoliaProduct } from '@jane/search/data-access';
import type { MenuProduct } from '@jane/shared/types';
import { isSoldByWeight, maybeParseFloat } from '@jane/shared/util';

export const getFilterForSpecialType = (
  specialType?: string,
  qualifyingStatus?: QualifyingStatusEnum
) => {
  switch (specialType) {
    case 'product': {
      return 'brand_special_ids';
    }
    case 'buy_x_and_save':
    case 'buy_x_get_y': {
      return qualifyingStatus === 'applied' || qualifyingStatus === 'qualified'
        ? 'bundle_brand_special_ids.discount'
        : 'bundle_brand_special_ids.qualify';
    }
    default: {
      return 'brand_special_ids';
    }
  }
};

/** Structural so both the v1 and the v3 cart product satisfy it. */
export interface WeightFilterCartProduct {
  price_id: PriceId;
  product_id: number;
}

const productInCart = (
  cartProducts: readonly WeightFilterCartProduct[],
  productId: number,
  priceId?: PriceId
) => {
  return cartProducts?.some(
    (cartProduct) =>
      cartProduct.product_id === productId &&
      (!priceId || cartProduct.price_id === priceId)
  );
};

interface ShouldShowWeight {
  cartProducts?: readonly WeightFilterCartProduct[];
  currentSpecial?: BrandSpecial;
  qualifyingStatus?: QualifyingStatusEnum;
}

/**
 * Creates a weight filtering function based on brand special conditions.
 * Returns a function that determines whether a specific weight should be shown,
 * handling cases like cart products, qualifying status, and brand special rules.
 * The special's applicable price ids are resolved once, so the returned function
 * can be reused cheaply across multiple weight checks. An empty list means the
 * special has no weight restriction.
 */
export const shouldShowWeight = ({
  cartProducts = [],
  currentSpecial,
  qualifyingStatus,
}: ShouldShowWeight) => {
  const applicablePriceIds = getPriceIdsForBrandSpecial(
    currentSpecial,
    qualifyingStatus
  );

  return (weight: string, product: MenuProduct | AlgoliaProduct) => {
    if (!currentSpecial) return true;

    const productId = 'id' in product ? product.id : product.product_id;
    const weightId = weight.replace(' ', '_') as ProductWeight;

    const stringPrice = product[`price_${weightId}`];
    const price = stringPrice && maybeParseFloat(stringPrice);

    // If the weight is included in the special but this product just doesn't have that weight.
    if (!price) return false;

    // If the product at this weight is in the cart, keep it.
    if (productInCart(cartProducts, productId, weightId)) return true;

    if (applicablePriceIds.length === 0) return true;

    return applicablePriceIds.includes(weightId);
  };
};

/**
 * Filters out products that would have no valid weights after applying brand special conditions.
 * Products in cart are always kept regardless of weight restrictions.
 */
export const filterProductsByWeight = ({
  cartProducts = [],
  currentSpecial,
  products,
  qualifyingStatus,
}: ShouldShowWeight & {
  products: SmartSortProduct<AlgoliaProduct>[];
}) => {
  if (!currentSpecial) return products;

  const weightFilter = shouldShowWeight({
    currentSpecial,
    cartProducts,
    qualifyingStatus,
  });

  return products.filter((product) => {
    const productKind = product.attributes['kind'];

    if (!isSoldByWeight(productKind)) return true;

    const productId = product.attributes['product_id'];

    const availableWeights = product.attributes['available_weights'];

    if (productInCart(cartProducts, productId)) return true;

    return (availableWeights || []).some((weight: string) => {
      const filter = weightFilter(weight, product.attributes);
      return filter;
    });
  });
};
